import { getUniqueID } from '../bundle-builder';

export const convertToInteger = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const number = parseInt(value, 10);
    return Number.isNaN(number) ? 0 : number;
  }
  return 0;
};

export const isCampaignSatisfy = (
  cart,
  campaignId,
  campaignType,
  purchaseThreshold,
  quantityThreshold,
  giftLimit,
  totalQuantityOfGiftProductsInCart,
  tagFilterType,
  allowedProductTags,
  excludedProductTags,
  attributeKey,
) => {
  const cartLines = cart?.state?.items;

  // this will only include the items that are allowed for the GWP campaign
  const allowedCartItems = getAllowedCartItems(cartLines, tagFilterType, allowedProductTags, excludedProductTags) || [];

  if (campaignType === 'purchase' && purchaseThreshold) {
    const cartTotal = allowedCartItems.reduce((acc, item) => acc + item.final_line_price / 100, 0);

    return (
      cartTotal &&
      cartTotal >= purchaseThreshold * (totalQuantityOfGiftProductsInCart + 1) &&
      totalQuantityOfGiftProductsInCart < giftLimit
    );
  }

  if (campaignType === 'quantity' && quantityThreshold) {
    const allowedQuantity = getAllowedGwpQuantity(allowedCartItems, quantityThreshold, attributeKey);

    return allowedQuantity > totalQuantityOfGiftProductsInCart && totalQuantityOfGiftProductsInCart < giftLimit;
  }

  return false;
};

// get cart items that are allowed to be included in GWP calculation
export const getAllowedCartItems = (cartLines, tagFilterType, allowedProductTags = [], excludedProductTags = []) =>
  tagFilterType === 'include' || tagFilterType === 'exclude'
    ? cartLines?.filter((item) => {
        const productTags = item?.properties?._product_tags?.split(',') || [];

        return (
          (tagFilterType === 'include' && allowedProductTags.some((tag) => productTags.includes(tag))) ||
          (tagFilterType === 'exclude' && !excludedProductTags.some((tag) => productTags.includes(tag)))
        );
      })
    : cartLines;

// determines how many GWP items are allowed in the cart based on the quantity of allowed cart items
export const getAllowedGwpQuantity = (allowedCartItems, quantityThreshold, attributeKey) => {
  let totalQuantity = 0;

  allowedCartItems?.forEach((item) => {
    // if item doesn't have "_gwp" attribute key, include it in the count
    if (!(attributeKey in item.properties)) {
      totalQuantity += item.quantity;
    }
  });

  // quantityThreshold is the threshold that needs to be met to qualify for the GWP campaign
  const totalGwpAllowedInCart = Math.floor(totalQuantity / quantityThreshold);
  return totalGwpAllowedInCart;
};

export const autoDeleteGwpFromCart = (
  gwpMultipleLineItems,
  campaignId,
  campaignType,
  purchaseThreshold,
  quantityThreshold,
  giftLimit,
  totalQuantityOfGiftProductsInCart,
  cart,
  tagFilterType,
  allowedProductTags,
  excludedProductTags,
  attributeKey,
) => {
  if (!gwpMultipleLineItems || gwpMultipleLineItems?.length === 0) return;
  const cartLines = cart?.state?.items;

  // this will only include the items that are allowed for the GWP campaign
  const allowedCartItems = getAllowedCartItems(cartLines, tagFilterType, allowedProductTags, excludedProductTags) || [];

  let allowedGwpQuantity = 0;

  if (campaignType === 'purchase' && purchaseThreshold) {
    const cartTotal = allowedCartItems.reduce((acc, item) => acc + item.final_line_price / 100, 0);
    allowedGwpQuantity = Math.floor(cartTotal / purchaseThreshold);
  } else if (campaignType === 'quantity' && quantityThreshold) {
    allowedGwpQuantity = getAllowedGwpQuantity(allowedCartItems, quantityThreshold, attributeKey);
  }

  allowedGwpQuantity = Math.min(allowedGwpQuantity, giftLimit || Infinity);

  if (totalQuantityOfGiftProductsInCart > allowedGwpQuantity) {
    const removePayload = [gwpMultipleLineItems[0].id];

    if (removePayload.length > 0) {
      cart.remove(removePayload);
    }
  }
};

export const handleAddGwpToCart = (product, attributeKey) => {
  const products = Array.isArray(product) ? product : [product];
  const payloads = products
    .filter((p) => p?.variantId)
    .map((p) => {
      const variant = { id: p.variantId };

      const attributes = [
        {
          key: '_gwp_id',
          value: getUniqueID(),
        },
        {
          key: attributeKey,
          value: true,
        },
      ];

      if (p.gwpCurrentId) {
        attributes.push({
          key: '_gwp_current_id',
          value: p.gwpCurrentId,
        });
      }

      const payload = window.getPayload(variant, 1);
      return {
        ...payload,
        attributes,
      };
    });

  if (payloads.length === 0) return;

  const addToCartEvent = new CustomEvent('cart:add', {
    detail: {
      payload: payloads,
    },
  });

  document.dispatchEvent(addToCartEvent);
};
